const MAGIC = 'MSUI';
const VERSION = 1;
const HEADER_SIZE = 7;

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64.replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function detectImageMime(bytes: Uint8Array): string {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    return 'image/jpeg';
  }
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50) {
    return 'image/png';
  }
  if (bytes.length >= 3 && bytes[0] === 0x47 && bytes[1] === 0x49) {
    return 'image/gif';
  }
  return 'image/jpeg';
}

function isPacked(bytes: Uint8Array): boolean {
  return bytes.length >= HEADER_SIZE
    && String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) === MAGIC
    && bytes[4] === VERSION;
}

export function packUniteImageWithFileName(fileName: string, imageBase64: string): string {
  const rawBase64 = imageBase64.includes(',') ? imageBase64.split(',')[1] : imageBase64;
  const imageBytes = base64ToBytes(rawBase64);
  const nameBytes = new TextEncoder().encode(fileName.slice(0, 65535));
  const packed = new Uint8Array(HEADER_SIZE + nameBytes.length + imageBytes.length);
  for (let i = 0; i < MAGIC.length; i += 1) {
    packed[i] = MAGIC.charCodeAt(i);
  }
  packed[4] = VERSION;
  packed[5] = nameBytes.length & 0xff;
  packed[6] = (nameBytes.length >> 8) & 0xff;
  packed.set(nameBytes, HEADER_SIZE);
  packed.set(imageBytes, HEADER_SIZE + nameBytes.length);
  return bytesToBase64(packed);
}

function normalizeImagePayload(payload: unknown): string {
  if (payload == null || payload === '') {
    return '';
  }
  if (typeof payload === 'string') {
    return payload;
  }
  if (Array.isArray(payload) && payload.length > 0 && typeof payload[0] === 'number') {
    return bytesToBase64(new Uint8Array(payload as number[]));
  }
  return '';
}

export function toUniteImageDisplaySrc(image: unknown): string {
  const normalized = normalizeImagePayload(image);
  return normalized ? parseUniteImagePayload(normalized).imageSrc : '';
}

export function toUniteImageFileName(image: unknown): string | undefined {
  const normalized = normalizeImagePayload(image);
  return normalized ? parseUniteImagePayload(normalized).fileName : undefined;
}

export function toUniteListImageSrcFromApi(data: unknown): string {
  if (data == null || data === '') {
    return '';
  }
  if (typeof data === 'string') {
    return parseUniteImagePayload(data).imageSrc;
  }
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return '';
    }
    if (typeof data[0] === 'number') {
      return toUniteImageDisplaySrc(data);
    }
  }
  return '';
}

export function parseUniteImagePayload(payload: string | undefined): {
  fileName?: string;
  imageSrc: string;
} {
  if (!payload) {
    return { imageSrc: '' };
  }

  if (payload.startsWith('data:')) {
    return { imageSrc: payload };
  }

  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(payload);
  } catch {
    return { imageSrc: `data:image/jpeg;base64,${payload}` };
  }

  if (!isPacked(bytes)) {
    const mime = detectImageMime(bytes);
    return { imageSrc: `data:${mime};base64,${payload}` };
  }

  const nameLen = bytes[5] | (bytes[6] << 8);
  const nameStart = HEADER_SIZE;
  const nameEnd = nameStart + nameLen;
  const fileName = new TextDecoder().decode(bytes.slice(nameStart, nameEnd));
  const imageBytes = bytes.slice(nameEnd);
  const mime = detectImageMime(imageBytes);
  const imageBase64 = bytesToBase64(imageBytes);

  return {
    fileName: fileName || undefined,
    imageSrc: `data:${mime};base64,${imageBase64}`,
  };
}
